import { toDateInputValue } from '../grid/utils/dateFormatters';
import type { ReviewedBankStatementTransaction, ReviewedStatementGroup } from './reviewHelpers';

export type StatementValidationErrors = Partial<
  Record<
    | 'statementDate'
    | 'company'
    | 'bankAccount'
    | 'currency'
    | 'fromDate'
    | 'toDate'
    | 'openingBalance'
    | 'closingBalance',
    string
  >
>;

export type TransactionValidationErrors = Partial<
  Record<'date' | 'type' | 'client' | 'description' | 'amount' | 'currency' | 'category', string>
>;

const isMissingNumber = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value);

export const validateStatement = (group: ReviewedStatementGroup): StatementValidationErrors => {
  const errors: StatementValidationErrors = {};

  if (!toDateInputValue(group.statementDate)) errors.statementDate = 'Statement Date is required';
  if (!group.CrmRelationId) errors.company = 'Company is required';
  if (!group.CrmRelationFinanceId) errors.bankAccount = 'Bank Account is required';
  if (!group.CrmCurrencyId) errors.currency = 'Currency is required';
  if (!toDateInputValue(group.fromDate)) errors.fromDate = 'From Date is required';
  if (!toDateInputValue(group.toDate)) errors.toDate = 'To Date is required';
  if (!errors.fromDate && !errors.toDate && group.fromDate > group.toDate) {
    errors.toDate = 'To Date cannot be before From Date';
  }
  if (isMissingNumber(group.openingBalance)) {
    errors.openingBalance = 'Opening Balance is required';
  }
  if (isMissingNumber(group.closingBalance)) {
    errors.closingBalance = 'Closing Balance is required';
  }

  return errors;
};

export const validateTransaction = (
  transaction: ReviewedBankStatementTransaction
): TransactionValidationErrors => {
  const errors: TransactionValidationErrors = {};
  const amount = transaction.Amount;

  if (!toDateInputValue(transaction.TransactionDate)) errors.date = 'Date is required';
  if (!transaction.IncomeExpenseType) errors.type = 'Type is required';
  if (!transaction.CounterpartyCrmRelationId && !transaction.CounterpartyRaw?.trim()) {
    errors.client = 'Client is required';
  }
  if (!transaction.TransactionDescription?.trim()) {
    errors.description = 'Description is required';
  }
  if (amount == null || !Number.isFinite(amount) || amount === 0) {
    errors.amount = 'Amount is required and must be non-zero';
  } else if (
    (amount > 0 && transaction.IncomeExpenseType !== 'Income') ||
    (amount < 0 && transaction.IncomeExpenseType !== 'Expense')
  ) {
    errors.type = 'Type must match the amount direction';
  }
  if (!transaction.CrmCurrencyId) errors.currency = 'Currency is required';
  if (!transaction.CrmIncomeExpenseCategoryId && !transaction.BankCategoryRaw?.trim()) {
    errors.category = 'Category is required';
  }
  return errors;
};

export const getImportDisabledReason = (
  hasPreview: boolean,
  statementGroups: ReviewedStatementGroup[],
  transactions: ReviewedBankStatementTransaction[]
): string | null => {
  if (!hasPreview) return 'Preview is required before import.';
  if (statementGroups.length === 0) return 'At least one transaction is required.';

  const statementErrors = statementGroups.map(validateStatement);
  if (
    statementErrors.some(
      (errors) =>
        errors.statementDate ||
        errors.fromDate ||
        errors.toDate ||
        errors.openingBalance ||
        errors.closingBalance
    )
  ) {
    return 'Statement Date, From Date, To Date, Opening Balance, and Closing Balance are required and must be valid for every statement.';
  }
  if (statementErrors.some((errors) => errors.company || errors.bankAccount || errors.currency)) {
    return 'Company, Bank Account, and Statement Currency must be resolved for every group.';
  }

  for (const transaction of transactions) {
    const errors = validateTransaction(transaction);
    if (errors.date) return 'Transaction Date is required and must be valid for every row.';
    if (errors.type === 'Type is required') return 'Transaction Type is required for every row.';
    if (errors.type) return 'Transaction Type must match the amount direction.';
    if (errors.client) return 'Client is required for every row.';
    if (errors.description) return 'Description is required for every row.';
    if (errors.amount) return 'Amount is required and must be non-zero for every row.';
    if (errors.currency) return 'Transaction Currency must be resolved for every row.';
    if (errors.category) return 'Category is required for every row.';
  }

  return null;
};
